import React, { useEffect, useRef } from "react";
import Logo from "../images/logo.png";
import LettuceTalkLogo from "../images/lettucetalk-logo.png";
import CardFrameImage from "../images/card-frame.png";
import MoonTogether from "../images/moon-together.png";

const SUBSTACK_URL = "https://impactmoonshots.substack.com";
const LETTUCETALK_URL = "https://lettucetalk.imoonshots.com";

const focusRing = "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-sky-200 focus-visible:ring-offset-2 focus-visible:ring-offset-[#f8fcff]";

const PUBLIC_WALL_CARDS = [
  {
    question: "What's a conversation you're glad we had as a family, even though it might have been difficult?",
    translation: "비록 어려웠을지라도, 가족으로서 나눠서 다행이라고 생각하는 대화는 무엇인가요?",
    top: "0%",
    rotate: "-8deg",
    sway: "10px",
    delay: "-2s"
  },
  {
    question: "Is there someone in your life who was good at listening to you? What did they do that made you feel heard?",
    translation: "在您的生活中，有没有人善于倾听您心声？他们做了什么让您感觉被倾听了?",
    top: "70%",
    rotate: "7deg",
    sway: "-12px",
    delay: "-16s"
  },
  {
    question: "What's a childhood memory that still brings you joy?",
    translation: "¿Cuál es un recuerdo de su infancia que todavía le trae alegría?",
    top: "12%",
    rotate: "-5deg",
    sway: "14px",
    delay: "-20s"
  },
  {
    question: "How should we signal when there's something one of us wants to say that's hard to bring up?",
    translation: "가족중 누군가가 꺼내기 어려운 이야기가 있을 때, 어떻게 다가가면 좋을까요?",
    top: "68%",
    rotate: "-5deg",
    sway: "14px",
    delay: "-30s"
  },
  {
    question: "When have you seen our family come together during challenging times?",
    translation: "어려운 시기에 우리 가족이 함께 뭉쳤던 때는 언제인가요?",
    top: "60%",
    rotate: "10deg",
    sway: "-9px",
    delay: "-44s"
  },

];

const SectionEyebrow = ({ children }) => (
  <p className="mb-4 text-[0.72rem] font-bold uppercase tracking-[0.28em] text-sky-700">
    {children}
  </p>
);

const PrimaryLink = ({ href, children, external = false, className = "" }) => (
  <a
    href={href}
    target={external ? "_blank" : undefined}
    rel={external ? "noopener noreferrer" : undefined}
    className={`inline-flex min-h-[48px] items-center justify-center rounded-full bg-gradient-to-r from-[#9a5eff] via-[#b077ff] to-[#c48cff] px-6 py-3 text-center text-[0.78rem] font-semibold uppercase tracking-[0.14em] text-white shadow-[0_12px_28px_rgba(121,70,250,0.26)] transition hover:-translate-y-0.5 hover:shadow-[0_16px_34px_rgba(121,70,250,0.32)] ${focusRing} ${className}`}
  >
    {children}
  </a>
);

const SecondaryLink = ({ href, children, external = false, className = "" }) => (
  <a
    href={href}
    target={external ? "_blank" : undefined}
    rel={external ? "noopener noreferrer" : undefined}
    className={`inline-flex min-h-[48px] items-center justify-center rounded-full bg-gradient-to-r from-[#9a5eff] via-[#b077ff] to-[#c48cff] px-6 py-3 text-center text-[0.78rem] font-semibold uppercase tracking-[0.14em] text-white shadow-[0_12px_28px_rgba(121,70,250,0.2)] transition hover:-translate-y-0.5 hover:shadow-[0_16px_34px_rgba(121,70,250,0.28)] ${focusRing} ${className}`}
  >
    {children}
  </a>
);

const ScrollReveal = ({ children, className = "" }) => {
  const revealRef = useRef(null);

  useEffect(() => {
    const element = revealRef.current;
    if (!element) return undefined;

    if (!("IntersectionObserver" in window)) {
      element.classList.add("reveal-visible");
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("reveal-visible");
        observer.unobserve(entry.target);
      },
      { threshold: 0.12, rootMargin: "0px 0px -48px" }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={revealRef} className={`reveal ${className}`}>
      {children}
    </div>
  );
};

const AnimatedCardWall = () => {
  const cardWidth = "clamp(150px, 40vw, 205px)";
  const cardHeight = "clamp(230px, 60vw, 320px)";

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      {PUBLIC_WALL_CARDS.map((card) => (
        <div
          key={card.question}
          className="motion-card absolute left-0 w-full"
          style={{
            top: card.top,
            animation: "wall-card-scroll 44s linear infinite",
            animationDelay: card.delay,
          }}
        >
          <div
            className="relative"
            style={{
              width: cardWidth,
              "--card-rotate": card.rotate,
              "--card-sway": card.sway,
              animation: "wall-card-sway 8s ease-in-out infinite",
              animationDelay: card.delay,
            }}
          >
            <div className="absolute left-1/2 top-1/2 h-36 w-36 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/80 blur-2xl" />
            <div
              className="relative drop-shadow-[0_16px_28px_rgba(70,93,126,0.18)]"
              style={{
                width: cardWidth,
                height: cardHeight,
                animation: "card-reveal 44s linear infinite",
                animationDelay: card.delay,
              }}
            >
              <img src={CardFrameImage} alt="" className="absolute inset-0 h-full w-full object-contain" />
              <div className="absolute inset-x-[17%] top-[13%] bottom-[22%] flex flex-col items-center justify-center gap-1.5 text-center px-1">
                <span className="text-[10px] leading-tight text-[#2f2a16] sm:text-[14px] font-bold" style={{ fontFamily: "Poppins, sans-serif" }}>
                  {card.question}
                </span>
                <span className="text-[7px] font-medium leading-snug text-[#5f5942] sm:text-[8px]">
                  {card.translation}
                </span>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default function Home() {
  return (
    <main id="main-content" className="min-h-screen overflow-hidden bg-[#f8fcff] font-round text-slate-900 antialiased">
      <style>
        {`
          @keyframes wall-card-scroll {
            0% { transform: translate3d(-240px, 0, 0); opacity: 0; }
            8% { opacity: 1; }
            86% { opacity: 1; }
            100% { transform: translate3d(calc(100% + 240px), 0, 0); opacity: 0; }
          }
          @keyframes wall-card-sway {
            0%, 100% { transform: translate3d(0, 0, 0) rotate(var(--card-rotate)); }
            30% { transform: translate3d(0, var(--card-sway), 0) rotate(calc(var(--card-rotate) + 1.6deg)); }
            62% { transform: translate3d(0, calc(var(--card-sway) * -0.7), 0) rotate(calc(var(--card-rotate) - 1.1deg)); }
          }
          @keyframes card-reveal {
            0%, 7% { opacity: 0; transform: translateY(10px) scale(0.95); }
            17%, 84% { opacity: 1; transform: translateY(0) scale(1); }
            100% { opacity: 0; transform: translateY(8px) scale(0.98); }
          }
          @keyframes cloud-drift {
            0%, 100% { transform: translate3d(-1.5%, 0, 0); }
            50% { transform: translate3d(1.5%, -10px, 0); }
          }
          html { scroll-behavior: smooth; }
        `}
      </style>

      {/* HERO */}
      <section aria-labelledby="hero-heading" className="relative isolate min-h-[100svh] overflow-hidden flex flex-col">
        <div className="absolute inset-0 -z-20 bg-[linear-gradient(180deg,#dff2ff_0%,#eef8ff_36%,#f8fcff_100%)]" />
        <div className="cloud-drift absolute inset-0 -z-10 bg-[radial-gradient(circle_at_16%_18%,rgba(255,255,255,0.95)_0%,rgba(255,255,255,0.65)_12%,transparent_24%),radial-gradient(circle_at_82%_24%,rgba(255,255,255,0.92)_0%,rgba(255,255,255,0.60)_12%,transparent_24%),radial-gradient(circle_at_50%_10%,rgba(186,230,253,0.5),transparent_34%)]" style={{ animation: "cloud-drift 24s ease-in-out infinite" }} />

        <nav aria-label="Primary navigation" className="mx-auto flex w-full max-w-7xl items-center justify-between gap-4 px-4 py-5 sm:px-8 sm:py-7 lg:px-10">
          <a href="#hero-heading" className={`flex min-w-0 items-center gap-3 rounded-full ${focusRing}`}>
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-white/70 bg-white/80 shadow-sm sm:h-14 sm:w-14">
              <img src={Logo} alt="" className="h-9 w-9 rounded-full object-cover sm:h-10 sm:w-10" />
            </span>
            <span className="hidden sm:block">
              <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-slate-900">Impact Moonshots</span>
            </span>
          </a>
          {/* <div className="hidden items-center gap-7 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600 lg:flex">
            <a href="#about" className={`hover:text-slate-900 ${focusRing}`}>About</a>
            <a href="#lettucetalk" className={`hover:text-slate-900 ${focusRing}`}>LettuceTalk</a>
          </div> */}
        </nav>

        <div className="flex-1 flex items-center justify-center">
          <div className="mx-auto max-w-5xl px-5 py-20 text-left sm:px-8 sm:py-28 lg:px-10">

            {/* <SectionEyebrow>An AI Product Studio</SectionEyebrow> */}
            <h1 id="hero-heading" className="text-4xl font-medium leading-[1.02] tracking-[-0.055em] text-slate-900 sm:text-6xl lg:text-7xl">
              AI infrastructure for life’s most <span className="text-sky-700">vulnerable</span> moments.
            </h1>
            <p className="mt-6 max-w-3xl text-base leading-7 text-slate-600 sm:text-xl sm:leading-8">
              We build joyful technology for moments when the stakes are high, capacity is low, and the systems people need are hardest to navigate.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:justify-start">
              <PrimaryLink href={LETTUCETALK_URL} external>Explore First Moonshot</PrimaryLink>
            </div>
          </div>
        </div>
        <a
          href="#about"
          className="mcaad-scroll-more absolute bottom-6 left-1/2 z-40 flex -translate-x-1/2 flex-col items-center gap-2 font-sans text-[10px] font-black uppercase tracking-[0.2em] text-[#2f2a16]/70 transition-colors hover:text-[#245c07]"
          aria-label="Scroll to learn more"
        >
          <span>Scroll</span>
          <span className="h-4 w-4 rotate-45 border-b-2 border-r-2 border-current" aria-hidden="true" />
        </a>
      </section>

      {/* ABOUT */}
      <section id="about" aria-labelledby="about-heading" className="relative overflow-hidden border-y border-sky-100 bg-[linear-gradient(135deg,#e6f5ff_0%,#f7fcff_56%,#f5faef_100%)] px-5 py-20 sm:px-8 sm:py-28 lg:px-10">
        <div className="absolute left-1/2 top-0 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full bg-sky-100/60 blur-3xl" />
        <ScrollReveal className="relative mx-auto max-w-4xl">
          <SectionEyebrow>Why We Build</SectionEyebrow>
          <h2 id="about-heading" className="max-w-3xl text-3xl font-medium leading-tight tracking-[-0.05em] text-slate-900 sm:text-5xl lg:text-6xl">
            Most technology starts after you know what to do. We start earlier.
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg sm:leading-8">
            In life’s most vulnerable moments, the hardest part is often getting to a clear next step at all. Stress, uncertainty, emotional pressure, and complex surrounding systems can make it harder to understand what matters, form a goal, or begin to act.
          </p>
          <p className="mt-5 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg sm:leading-8">
            Impact Moonshots builds technology for that gap—helping people move from overwhelm and uncertainty toward clearer choices and action.
          </p>
          <p className="mt-6 max-w-2xl text-base leading-7 text-slate-900 sm:text-lg sm:leading-8">
            Our first moonshot starts with one of the most consequential things people do when the stakes are high: <strong>talk</strong>.
          </p>
        </ScrollReveal>
      </section>

      {/* LETTUCETALK */}
      <section id="lettucetalk" aria-labelledby="lettucetalk-heading" className="relative scroll-mt-8 overflow-hidden min-h-[90svh] bg-[#fbf4d6] shadow-[inset_0_0_70px_rgba(255,255,255,0.6)] px-5 py-20 sm:px-8 sm:py-24 lg:px-10 flex flex-col items-center">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_20%,rgba(255,255,255,0.98)_0%,rgba(255,255,255,0.7)_13%,transparent_31%),radial-gradient(circle_at_52%_45%,rgba(255,255,255,0.62)_0%,rgba(255,255,255,0.28)_22%,transparent_42%),radial-gradient(circle_at_30%_55%,rgba(229,195,79,0.34),transparent_30%),radial-gradient(circle_at_82%_66%,rgba(255,255,255,0.34),transparent_28%),linear-gradient(180deg,#fff7d2_0%,#f4e7af_55%,#efe1b5_100%)]" />
        <div className="absolute left-1/2 top-[18%] aspect-square w-80 -translate-x-1/2 rounded-full border border-white/50 bg-white/20 shadow-[0_0_95px_rgba(255,255,255,0.9)] sm:w-[430px]" />
        <div className="absolute left-[18%] top-[30%] aspect-square w-36 rounded-full bg-white/34 blur-2xl sm:w-52" />
        <div className="absolute right-[12%] top-[36%] aspect-square w-40 rounded-full bg-[#fff8cf]/42 blur-2xl sm:w-60" />
        <div className="absolute left-[42%] top-[58%] aspect-square w-44 -translate-x-1/2 rounded-full bg-white/28 blur-3xl sm:w-64" />
        <div className="absolute right-[16%] bottom-[12%] aspect-square w-32 rounded-full bg-[#fffdf1]/34 blur-2xl sm:w-48" />
        <div className="absolute left-1/2 top-[36%] aspect-square w-56 -translate-x-1/2 rounded-full bg-[#fff4ad]/22 blur-3xl sm:w-72" />
        <div className="absolute inset-x-0 top-0 h-56 bg-gradient-to-b from-[#fff8d8] via-[#fff8d8]/72 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-[#efe1b5] via-[#efe1b5]/82 to-transparent" />
        <AnimatedCardWall />
        <ScrollReveal className="relative mx-auto w-full max-w-7xl">
          <div className="mx-auto max-w-3xl">
            <div className="relative mt-5 rounded-[2.25rem] border border-[#f4d94e]/60 bg-[radial-gradient(circle_at_50%_0%,rgba(255,255,255,0.96)_0%,rgba(255,255,255,0.74)_30%,rgba(255,248,190,0.58)_60%,rgba(255,255,255,0.54)_100%)] px-6 pb-10 pt-16 shadow-[0_30px_90px_rgba(78,67,24,0.16),inset_0_0_36px_rgba(255,255,255,0.72)] backdrop-blur-xl">
              <div className="pointer-events-none absolute -inset-px rounded-[2.25rem] bg-[linear-gradient(135deg,rgba(255,255,255,0.78),transparent_38%,rgba(244,217,78,0.28)_72%,rgba(255,255,255,0.66))] opacity-80" />
              <div className="pointer-events-none absolute left-1/2 top-0 h-8 w-28 -translate-x-1/2 -translate-y-[40%] rounded-full border border-[#f4d94e]/38 bg-[#fff7b8]/55 shadow-[0_0_34px_rgba(244,217,78,0.48),inset_0_0_16px_rgba(255,255,255,0.7)]" />
              <div className="absolute left-1/2 top-0 z-10 flex h-24 w-24 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/80 bg-[radial-gradient(circle_at_34%_24%,rgba(255,255,255,0.98)_0%,rgba(255,255,255,0.78)_34%,rgba(255,253,241,0.42)_66%,rgba(255,244,140,0.2)_100%)] shadow-[0_0_54px_rgba(255,255,255,0.95),0_0_42px_rgba(244,217,78,0.55),0_18px_42px_rgba(78,67,24,0.18),inset_0_0_18px_rgba(255,255,255,0.92)] backdrop-blur-md sm:h-28 sm:w-28">
                <img src={LettuceTalkLogo} alt="LettuceTalk" className="relative h-[72px] w-[72px] object-contain drop-shadow-[0_8px_14px_rgba(48,118,9,0.2)] sm:h-[86px] sm:w-[86px] p-1" />
              </div>
              <div className="relative z-[1] space-y-4 text-center">
                <p className="mx-auto text-sm font-semibold uppercase tracking-[0.35em] text-slate-900 sm:text-base">
                  Moonshot No. 1
                </p>
                <h2 id="lettucetalk-heading" className="text-3xl font-bold tracking-[-0.055em] sm:text-5xl lg:text-7xl" style={{ fontFamily: "Dynapuff, sans-serif", color: "#4D7C0F" }}>
                  LettuceTalk
                </h2>
                <p className="mx-auto max-w-[520px] text-sm font-semibold tracking-[0.12em] text-[#4f5824] sm:text-base">
                  A gentler way to start difficult family caregiving conversations.
                </p>
                <p className="px-8 text-base leading-7 text-gray-700 sm:text-lg text-left">
                  When someone you love needs care, knowing what to say can be surprisingly hard. LettuceTalk helps turn a worry, question, or hard-to-say thought into a conversation you can start—with multilingual conversation cards and an AI-personalized digital experience.
                </p>
                <p className="px-8 text-base leading-7 text-gray-700 sm:text-lg text-left">
                  Start with a ready-made prompt—or enter a question, worry, or hard-to-say thought and receive three gentle ways to ask, with optional translation.
                </p>
                <PrimaryLink href={LETTUCETALK_URL} external className="inline-flex w-full justify-center sm:w-auto">
                  Explore LettuceTalk
                </PrimaryLink>
                <div className="grid gap-4 border-t border-[#d8c96c]/70 pt-7 text-left sm:grid-cols-3 sm:gap-5">
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#fff8c7] text-[#8d7b18] shadow-sm" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" stroke="currentColor" strokeWidth="1.7">
                        <path d="m12 3 1.25 3.75L17 8l-3.75 1.25L12 13l-1.25-3.75L7 8l3.75-1.25L12 3Z" />
                        <path d="m18.5 13 .65 1.85L21 15.5l-1.85.65-.65 1.85-.65-1.85L16 15.5l1.85-.65.65-1.85Z" />
                        <path d="M5 14v5m-2.5-2.5h5" />
                      </svg>
                    </span>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-900">Sold Out</p>
                      <p className="mt-1 text-sm leading-5 text-[#5f5942]">Initial museum inventory in Washington, DC</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#fff8c7] text-[#8d7b18] shadow-sm" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" stroke="currentColor" strokeWidth="1.7">
                        <circle cx="12" cy="12" r="8.5" />
                        <path d="M3.8 9h16.4M3.8 15h16.4M12 3.5c2.2 2.35 3.25 5.18 3.25 8.5S14.2 18.15 12 20.5c-2.2-2.35-3.25-5.18-3.25-8.5S9.8 5.85 12 3.5Z" />
                      </svg>
                    </span>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-900">4 Languages</p>
                      <p className="mt-1 text-sm leading-5 text-[#5f5942]">Designed for multilingual families</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#fff8c7] text-[#8d7b18] shadow-sm" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" stroke="currentColor" strokeWidth="1.7">
                        <rect x="4" y="4" width="6.5" height="6.5" rx="1.2" />
                        <rect x="13.5" y="4" width="6.5" height="6.5" rx="1.2" />
                        <rect x="4" y="13.5" width="6.5" height="6.5" rx="1.2" />
                        <rect x="13.5" y="13.5" width="6.5" height="6.5" rx="1.2" />
                      </svg>
                    </span>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-900">Physical + Digital</p>
                      <p className="mt-1 text-sm leading-5 text-[#5f5942]">From conversation cards to personalized digital support</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </section>

      {/* HUMAN IMPACT */}
      <section aria-labelledby="human-impact-heading" className="border-t border-[#e4ead4] bg-[linear-gradient(135deg,#f7fbf1_0%,#fbfcf5_55%,#f1f7e8_100%)] px-5 py-16 sm:px-8 sm:py-20 lg:px-10">
        <ScrollReveal className="mx-auto max-w-7xl">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 text-[#6f9250]" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" className="h-7 w-7" stroke="currentColor" strokeWidth="1.6">
                <path d="M20.8 8.8c0 5.2-8.8 10.2-8.8 10.2S3.2 14 3.2 8.8A4.3 4.3 0 0 1 12 6.5a4.3 4.3 0 0 1 8.8 2.3Z" />
              </svg>
            </span>
            <div>
              <h2 id="human-impact-heading" className="text-2xl font-semibold tracking-[-0.04em] text-[#2f4431] sm:text-3xl">Human impact</h2>
              <p className="mt-1 text-sm font-medium text-[#52624e] sm:text-base">Real families. Real conversations. Real change.</p>
            </div>
          </div>

          <div className="mt-8 grid gap-8 lg:grid-cols-2 lg:gap-10">
            <article className="rounded-2xl border border-[#e3e9d5] bg-white p-5 shadow-sm sm:p-6">
              <div>
                <h3 className="text-lg font-semibold leading-6 text-[#2f4431] sm:text-xl">After a sudden stroke, one Taiwanese father lost much of his English.</h3>
                <p className="mt-3 text-base leading-7 text-[#455144] sm:text-lg sm:leading-8">
                  His daughter and wife used LettuceTalk to better understand his care wishes and communicate with him as he recovered.
                </p>
              </div>
            </article>

            <article className="rounded-2xl border border-[#e3e9d5] bg-white p-5 shadow-sm sm:p-6">
              <div>
                <h3 className="text-lg font-semibold leading-6 text-[#2f4431] sm:text-xl">“I wish I’d had these before my mom died.”</h3>
                <p className="mt-3 text-base leading-7 text-[#455144] sm:text-lg sm:leading-8">
                  A middle-aged cashier choked up while flipping through the cards. His Filipino mother had passed away a few years earlier, before he had asked the questions he still wished he could ask.
                </p>
              </div>
            </article>

            <article className="rounded-2xl border border-[#e3e9d5] bg-white p-5 shadow-sm sm:p-6">
              <div>
                <h3 className="text-lg font-semibold leading-6 text-[#2f4431] sm:text-xl">One conversation changed the shape of a family’s caregiving.</h3>
                <p className="mt-3 text-base leading-7 text-[#455144] sm:text-lg sm:leading-8">
                  A Korean-American daughter used LettuceTalk to finally ask her brother abroad to share responsibility for their mother’s care. He eventually moved home.
                </p>
              </div>
            </article>

            <article className="rounded-2xl border border-[#e3e9d5] bg-white p-5 shadow-sm sm:p-6">
              <div>
                <h3 className="text-lg font-semibold leading-6 text-[#2f4431] sm:text-xl">And sometimes the conversation becomes public.</h3>
                <p className="mt-3 text-base leading-7 text-[#455144] sm:text-lg sm:leading-8">
                  At the Hollywood &amp; Mind Summit, PBS Caregiving executive producer Tom Chiodo used a LettuceTalk card onstage with Holly Robinson Peete to open a conversation about caregiving.
                </p>
              </div>
            </article>
          </div>
        </ScrollReveal>
      </section>

      {/* BEYOND CAREGIVING */}
      <section aria-labelledby="beyond-caregiving-heading" className="border-t border-sky-100 bg-[#f8fcff] px-5 py-20 sm:px-8 sm:py-24 lg:px-10">
        <ScrollReveal className="mx-auto max-w-4xl">
          <SectionEyebrow>Beyond caregiving</SectionEyebrow>
          <h2 id="beyond-caregiving-heading" className="max-w-3xl text-3xl font-medium leading-tight tracking-[-0.05em] text-slate-900 sm:text-5xl lg:text-6xl">
            One moonshot. A much larger need.
          </h2>
          <div className="mt-6 max-w-3xl space-y-5 text-base leading-7 text-slate-600 sm:text-lg sm:leading-8">
            <p>
              LettuceTalk begins with family caregiving, but the underlying challenge appears across many of life's hardest moments: people need to make consequential decisions precisely when uncertainty, stress, and complexity make action harder.
            </p>
            <p>
              Impact Moonshots is building toward a new class of technology designed for those moments—from recognizing what matters to helping people take the next step.
            </p>
          </div>
        </ScrollReveal>
      </section>

      {/* FOUNDER */}
      <section aria-labelledby="founder-heading" className="border-t border-sky-100 bg-[#eef8ff] px-5 py-8 sm:px-8 sm:py-10 lg:px-10">
        <ScrollReveal className="mx-auto max-w-7xl rounded-[1.5rem] border border-sky-100/80 bg-white/80 px-6 py-5 shadow-[0_16px_48px_rgba(70,93,126,0.08)] backdrop-blur-xl sm:px-8 sm:py-6">
          <div className="grid gap-4">
            <div>
              <h2 id="founder-heading" className="text-2xl font-medium leading-tight tracking-[-0.05em] text-slate-900 sm:text-3xl">
                Founded by Michelle Park
              </h2>
            </div>
            <div>
              <p className="max-w-3xl text-sm leading-6 text-slate-600 sm:text-base sm:leading-7">
                Impact Moonshots was founded by Michelle Park, a technologist and senior government advisor who has spent the last decade building technology across caregiving, public health, behavioral healthcare, and privacy for domestic violence survivors.
              </p>
              <SecondaryLink href="https://parkmichelle.com" external className="mt-4">About Michelle</SecondaryLink>
            </div>
          </div>
        </ScrollReveal>
      </section>

      {/* CTA */}
      <section aria-labelledby="cta-heading" className="border-t border-sky-100 bg-[#eef8ff] px-5 py-12 sm:px-8 sm:py-16 lg:px-10">
        <ScrollReveal className="mx-auto grid max-w-7xl overflow-hidden rounded-[2rem] border border-[#e3e9d5] bg-[linear-gradient(135deg,rgba(255,255,255,0.94),rgba(247,250,238,0.9))] shadow-[0_20px_60px_rgba(76,96,52,0.1)] lg:grid-cols-[minmax(260px,0.85fr)_minmax(0,1.65fr)]">
          <div className="relative min-h-56 overflow-hidden bg-[#e7f1de] sm:min-h-64 lg:min-h-full">
            <img src={MoonTogether} alt="A moon and small green planet together" className="h-full w-full object-cover" />
          </div>
          <div className="p-7 text-left sm:p-10">
            <SectionEyebrow>Join Our Journey</SectionEyebrow>
            <h2 id="cta-heading" className="max-w-2xl text-3xl font-medium leading-tight tracking-[-0.045em] text-slate-900 sm:text-5xl">Building for the moments that matter most.</h2>
            <p className="mt-5 max-w-2xl text-sm leading-6 text-slate-600 sm:text-base sm:leading-7">
              Follow our journey as we design for life's most vulnerable moments, one thoughtful system at a time.
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
              <PrimaryLink href={LETTUCETALK_URL} external>Explore LettuceTalk</PrimaryLink>
              <SecondaryLink href={SUBSTACK_URL} external className="!border !border-slate-900 !bg-transparent !bg-none !text-slate-900 !shadow-none hover:!bg-transparent hover:!bg-none">View Newsletter</SecondaryLink>
            </div>
          </div>
        </ScrollReveal>
      </section>

      <footer className="border-t border-slate-200 bg-white px-5 py-8 text-slate-600 sm:px-8 lg:px-10">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 text-sm sm:flex-row sm:items-center sm:justify-between">
          <p className="text-slate-600">© {new Date().getFullYear()} Impact Moonshots. All rights reserved.</p>
          <nav aria-label="Footer navigation" className="flex flex-wrap gap-x-5 gap-y-3 text-sm">
            <a href={LETTUCETALK_URL} target="_blank" rel="noopener noreferrer" className="hover:text-slate-900">LettuceTalk</a>
          </nav>
        </div>
      </footer>
    </main>
  );
}
